import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Paperclip, Send, Sparkles, X } from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';
import {
  sendMilliMessage,
  loadConversationHistory,
  resolveSessionId,
  getStoredSessionId,
  storeSessionId,
  listConversations,
  touchConversation,
  roomLabel,
  filesToAttachments,
  ATTACHMENT_ACCEPT,
  MAX_ATTACHMENTS,
} from '@/lib/milliData';

/* Milli dock — the K.O. Command orchestrator available on every dashboard
 * page, living in the right-hand space wide monitors leave empty.
 *
 * Deliberate constraints:
 *   - Desktop only (hidden below xl): phones never had the dead space, and
 *     they already have the full Milli page in the sidebar.
 *   - Owner only: rendered after a client-side _kc_is_owner() check, so a
 *     future tenant's admin never sees a dock that would only 403 at them
 *     (the milli-chat function re-checks server-side regardless).
 *   - Same conversation as her console (/milli): both open to the owner's
 *     latest thread from the server (resolveSessionId), so a thought
 *     started on the Estimates page continues on her page — and on the
 *     phone — mid-thread.
 *   - Self-contained: one component, one mount line in DashboardLayout.
 *     K.O. Command track owns this file (see CLAUDE.md).
 *
 * Palette matches the cockpit: charcoal steel, dark-bronze hairlines,
 * weathered copper. */

const OPEN_KEY = 'milli_dock_open';

export default function MilliDock() {
  const { t } = useTranslation();
  const location = useLocation();

  const [isOwner, setIsOwner] = useState(false);
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem(OPEN_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [messages, setMessages] = useState([]);
  const [hydrated, setHydrated] = useState(false);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [attachments, setAttachments] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [roomId, setRoomId] = useState(() => getStoredSessionId());
  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  const thinkingRef = useRef(false); // for the sync routine's window listeners
  const roomIdRef = useRef(roomId);
  useEffect(() => {
    roomIdRef.current = roomId;
  }, [roomId]);

  // Render only for K.O. Command owners — the same server-side rule the
  // chat function enforces, checked once up front so nobody else ever
  // sees the handle.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase.rpc('_kc_is_owner');
        if (!cancelled && data === true) setIsOwner(true);
      } catch {
        /* stay hidden */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const refreshBadge = useCallback(async () => {
    try {
      const { count } = await supabase
        .from('kc_approvals')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'pending');
      setPendingApprovals(count || 0);
    } catch {
      /* badge degrades to 0 */
    }
  }, []);

  useEffect(() => {
    if (isOwner) refreshBadge();
  }, [isOwner, refreshBadge]);

  useEffect(() => {
    thinkingRef.current = thinking;
  }, [thinking]);

  // Same conversation as her page and the phone: each time the panel opens
  // or the window comes back into view, show the owner's latest thread from
  // the server (see resolveSessionId). Never while her reply is in flight
  // here; this panel's own error rows are kept, at the bottom.
  const syncConversation = useCallback(async () => {
    if (thinkingRef.current) return;
    try {
      const id = await resolveSessionId();
      if (!id) return;
      const turns = await loadConversationHistory(id);
      if (thinkingRef.current) return;
      if (roomIdRef.current !== id) setMessages(turns);
      else if (turns.length) setMessages((prev) => [...turns, ...prev.filter((m) => m.role === 'error')]);
      setRoomId(id);
      try {
        setRooms(await listConversations(false));
      } catch {
        /* picker keeps what it had */
      }
    } catch {
      /* keep what's on screen */
    } finally {
      setHydrated(true);
    }
  }, []);

  const switchRoom = async (id) => {
    if (!id || id === roomId || thinking) return;
    setRoomId(id);
    storeSessionId(id);
    setMessages([]);
    touchConversation(id);
    try {
      setMessages(await loadConversationHistory(id));
    } catch {
      /* empty room view */
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    syncConversation();
    const onVisible = () => {
      if (document.visibilityState === 'visible') syncConversation();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [open, syncConversation]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, thinking, open]);

  // Esc collapses the panel from anywhere — the keyboard version of the
  // close button, for the same reason it exists.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') toggle();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = () => {
    setOpen((cur) => {
      const next = !cur;
      try {
        localStorage.setItem(OPEN_KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const pickFiles = async (e) => {
    // Copy before the reset: Chromium empties the live FileList when the
    // input's value is cleared (same fix as useMilliChat).
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    const { attachments: got, errors } = await filesToAttachments(
      files,
      MAX_ATTACHMENTS - attachments.length
    );
    if (got.length) {
      setAttachments((cur) => [...cur, ...got].slice(0, MAX_ATTACHMENTS));
    }
    for (const text of errors) {
      setMessages((cur) => [...cur, { role: 'error', text }]);
    }
  };

  const send = async () => {
    const text = input.trim();
    if ((!text && attachments.length === 0) || thinking) return;
    const atts = attachments;
    setAttachments([]);
    setInput('');
    setMessages((cur) => [
      ...cur,
      { role: 'user', text: text || '(attachment)', atts: atts.map((a) => a.name) },
    ]);
    setThinking(true);
    try {
      const res = await sendMilliMessage(text, getStoredSessionId(), atts);
      if (res.session_id) {
        storeSessionId(res.session_id);
        setRoomId(res.session_id);
      }
      setMessages((cur) => [...cur, { role: 'milli', text: res.reply, department: res.department ?? null }]);
      refreshBadge();
      try {
        setRooms(await listConversations(false));
      } catch {
        /* ignore */
      }
    } catch (e) {
      setMessages((cur) => [...cur, { role: 'error', text: e?.message || t('milli.sendError') }]);
    } finally {
      setThinking(false);
    }
  };

  // Her console (/milli) lives outside this layout, so the dock never
  // renders there; the old cockpit address redirects to it.
  if (!isOwner || location.pathname.startsWith('/dashboard/milli')) return null;

  return (
    <div className="hidden xl:block">
      {/* Collapsed handle — right edge, vertically centered */}
      {!open ? (
        <button
          type="button"
          onClick={toggle}
          className="fixed right-0 top-1/2 -translate-y-1/2 z-40 flex flex-col items-center gap-1.5 rounded-l-lg border border-r-0 border-[#3a332a] bg-[#1e1f21] px-2 py-3.5 text-[#c9873e] hover:bg-[#26272a] transition-colors shadow-lg"
          data-testid="milli-dock-handle"
          aria-label={t('milli.dock.open')}
        >
          <Sparkles size={16} />
          {pendingApprovals > 0 ? (
            <span className="min-w-[18px] rounded-full bg-amber-400 text-[#17181a] text-[10px] font-bold leading-[18px] text-center">
              {pendingApprovals}
            </span>
          ) : null}
        </button>
      ) : (
        <div
          className="fixed right-0 top-0 bottom-0 z-40 w-[380px] flex flex-col border-l border-[#3a332a] bg-gradient-to-b from-[#1b1c1e] to-[#131416] text-stone-200 shadow-2xl"
          data-testid="milli-dock-panel"
        >
          {/* Header */}
          <div className="flex items-center gap-2 border-b border-[#3a332a] px-4 py-3">
            <Sparkles size={14} className="text-[#c9873e]" />
            <span className="font-mono text-xs tracking-[0.24em] text-[#c9873e]">MILLI</span>
            {pendingApprovals > 0 ? (
              <Link
                to="/dashboard/approvals"
                className="ml-1 rounded-full bg-amber-400/15 border border-amber-400/40 px-2 py-0.5 text-[10px] font-mono text-amber-300 hover:bg-amber-400/25"
                data-testid="milli-dock-approvals-badge"
              >
                {pendingApprovals} {t('milli.dock.pending')}
              </Link>
            ) : null}
            <Link
              to="/milli"
              className="ml-auto font-mono text-[10px] uppercase tracking-wider text-stone-500 hover:text-[#c9873e]"
              data-testid="milli-dock-fullpage"
            >
              {t('milli.dock.fullPage')}
            </Link>
            {/* The close control is a labeled, bordered button on purpose:
                an icon-only chevron beside the full-page link read as one
                control, and the only obvious exits left were the browser's
                own window buttons — which minimize the whole app. */}
            <button
              type="button"
              onClick={toggle}
              className="ml-2 flex items-center gap-1 rounded border border-[#3a332a] px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-stone-300 hover:text-[#c9873e] hover:border-[#c9873e]/50 transition-colors"
              data-testid="milli-dock-close"
              aria-label={t('milli.dock.close')}
            >
              <X size={11} />
              {t('milli.dock.closeShort')}
            </button>
          </div>

          {/* Room picker — same rooms as her page; switching here switches everywhere */}
          {rooms.length > 0 ? (
            <div className="border-b border-[#3a332a] px-3 py-2">
              <select
                value={roomId ?? ''}
                onChange={(e) => switchRoom(e.target.value)}
                disabled={thinking}
                className="w-full rounded-md bg-[#17181a] border border-[#3a332a] px-2 py-1.5 text-xs text-stone-200 focus:outline-none focus:border-[#c9873e]/60 disabled:opacity-60"
                data-testid="milli-dock-room-select"
                aria-label={t('milli.rooms.label')}
              >
                {roomId && !rooms.some((r) => r.session_id === roomId) ? (
                  <option value={roomId}>{t('milli.rooms.untitled')}</option>
                ) : null}
                {rooms.map((r) => (
                  <option key={r.session_id} value={r.session_id}>
                    {roomLabel(r, t('milli.rooms.untitled'))}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          {/* Transcript */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="milli-dock-messages">
            {messages.length === 0 ? (
              <p className="mt-8 px-3 text-center text-xs text-stone-500 leading-relaxed">
                {hydrated ? t('milli.dock.emptyHint') : t('milli.historyLoading')}
              </p>
            ) : (
              messages.map((m, i) => {
                if (m.role === 'user') {
                  return (
                    <div key={i} className="flex justify-end">
                      <div className="max-w-[90%] rounded-lg bg-[#2b2d2f] px-3 py-2 text-[13px] whitespace-pre-wrap">
                        {m.atts?.length ? (
                          <div className="mb-1 flex flex-wrap gap-1">
                            {m.atts.map((name, j) => (
                              <span key={j} className="inline-flex items-center gap-1 rounded border border-[#3a332a] bg-[#17181a] px-1.5 py-0.5 font-mono text-[9px] text-[#c9873e]">
                                <Paperclip size={9} />{name}
                              </span>
                            ))}
                          </div>
                        ) : null}
                        {m.text}
                      </div>
                    </div>
                  );
                }
                if (m.role === 'error') {
                  return (
                    <div key={i} className="max-w-[90%] rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-[13px] text-red-300 whitespace-pre-wrap">
                      {m.text}
                    </div>
                  );
                }
                return (
                  <div key={i} className="max-w-[90%]">
                    {m.department ? (
                      <div className="font-mono text-[9px] tracking-[0.2em] uppercase mb-0.5" style={{ color: m.department.hex }}>
                        {m.department.name}
                      </div>
                    ) : null}
                    <div
                      className="rounded-lg border border-[#3a332a] bg-[#17181a] px-3 py-2 text-[13px] whitespace-pre-wrap leading-relaxed"
                      style={m.department ? { borderLeft: `3px solid ${m.department.hex}` } : undefined}
                    >
                      {m.text}
                    </div>
                  </div>
                );
              })
            )}
            {thinking ? (
              <div className="flex items-center gap-2 font-mono text-[10px] text-[#c9873e]">
                <span className="h-1.5 w-1.5 rounded-full bg-[#c9873e] animate-ping" />
                {t('milli.thinking')}
              </div>
            ) : null}
          </div>

          {/* Composer */}
          {attachments.length ? (
            <div className="border-t border-[#3a332a] px-2.5 pt-2 flex flex-wrap gap-1.5">
              {attachments.map((a, i) => (
                <span key={i} className="inline-flex items-center gap-1 rounded border border-[#c9873e]/40 bg-[#17181a] px-1.5 py-0.5 font-mono text-[9px] text-[#c9873e]">
                  <Paperclip size={9} />{a.name}
                  <button
                    type="button"
                    onClick={() => setAttachments((cur) => cur.filter((_, j) => j !== i))}
                    className="text-stone-500 hover:text-red-400"
                    aria-label="Remove attachment"
                  >
                    <X size={9} />
                  </button>
                </span>
              ))}
            </div>
          ) : null}
          <div className={`p-2.5 flex gap-2 ${attachments.length ? '' : 'border-t border-[#3a332a]'}`}>
            <input ref={fileRef} type="file" accept={ATTACHMENT_ACCEPT} multiple hidden onChange={pickFiles} />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={thinking || attachments.length >= MAX_ATTACHMENTS}
              className="rounded-md border border-[#3a332a] text-stone-400 px-2.5 hover:text-[#c9873e] hover:border-[#c9873e]/50 disabled:opacity-40 transition-colors"
              data-testid="milli-dock-attach"
              aria-label="Attach a photo or PDF"
            >
              <Paperclip size={14} />
            </button>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              rows={1}
              placeholder={t('milli.inputPlaceholder')}
              className="flex-1 resize-none rounded-md bg-[#17181a] border border-[#3a332a] px-3 py-2 text-[13px] text-stone-200 placeholder:text-stone-600 focus:outline-none focus:border-[#c9873e]/60"
              data-testid="milli-dock-input"
            />
            <button
              type="button"
              onClick={send}
              disabled={thinking || !input.trim()}
              className="rounded-md border border-[#c9873e]/50 text-[#c9873e] px-3 hover:bg-[#c9873e]/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              data-testid="milli-dock-send"
              aria-label={t('milli.send')}
            >
              <Send size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
